import userService from './user.service';
import css from './userActivation.module.less';
import { authService } from '@/auth/auth.service';
import { useUrlParams } from '@/lib/hooks';
import { t } from '@/translations/translator';
import { submitClosestForm, submitOnEnter, useFieldFeedback } from '@/util/formFeedback';
import { Button } from '@whiz-cart/ui-shared/button/button.component.tsx';
import { PasswordField } from '@whiz-cart/ui-shared/form/passwordField.tsx';
import { commonValidations } from '@whiz-cart/ui-shared/validations/commonValidations';
import { createForm } from 'cross-state/react';
import _ from 'lodash';
import { useEffect } from 'react';

const errorCodes = {
    invalidToken: 34006,
};

const { Form, Field, FormState } = createForm<{ password: string }>({
    defaultValue: { password: '' },
    validations: function* ({ draft, fieldError }) {
        if (!commonValidations.password(draft.password)) yield fieldError('password', 'length');
    },
});

export default function UserActivation() {
    const { token, type, userName } = useUrlParams(['token', 'type', 'userName']);
    const feedback = useFieldFeedback<'password'>();

    const { language: activationLanguage } = useUrlParams(['language']);
    useEffect(() => {
        if (activationLanguage) {
            authService.updateLanguage(activationLanguage);
        }
    }, [activationLanguage]);

    return (
        <Form
            className={css.view}
            reportValidity={false}
            onKeyDown={submitOnEnter}
            onSubmit={async (_event, { draft }) => {
                try {
                    await userService.activate({
                        token: token ?? '',
                        userName: userName ?? '',
                        password: draft.password,
                        type: type ?? '',
                    });
                } catch (e) {
                    console.error('Failed to active user', e);

                    const knownError = _.findKey(errorCodes, (x) => x === (e as any).data?.status) as keyof typeof errorCodes | undefined;
                    if (knownError) feedback.setError('password', t(`profile.activation.${knownError}`));
                    else feedback.setError('password', t('profile.login.unknownError'));
                }
            }}
        >
            <div className={css.title}>{t.unknown(`profile.activation.${type}`, undefined, { fallback: 'invalid type' })}</div>

            <label>{t('profile.userName')}</label>
            <div className={css.value}>{userName}</div>

            <label htmlFor="password" className={css.label}>
                {t('profile.newPassword')}
            </label>
            <Field
                name="password"
                render={({ value, onChange }, { errors, hasTriggeredValidations }) => (
                    <>
                        <PasswordField
                            id="password"
                            value={value}
                            onChange={(e) => {
                                onChange(e.currentTarget.value);
                                feedback.changed('password');
                            }}
                            autoComplete="new-password"
                            reveal
                        />
                        <div className={css.error}>
                            {feedback.error(
                                'password',
                                errors.length > 0 && t('validation.length', { length: value.length }),
                                hasTriggeredValidations,
                            )}
                        </div>
                    </>
                )}
            />

            <FormState selector={({ isValid, saveInProgress }) => ({ isValid, saveInProgress })}>
                {({ isValid, saveInProgress }) => (
                    <Button
                        className={css.button}
                        primary
                        onClick={submitClosestForm}
                        inactive={!isValid || saveInProgress}
                        spinner={saveInProgress}
                    >
                        {t('profile.activation.submit')}
                    </Button>
                )}
            </FormState>
        </Form>
    );
}
